import {
  EventType,
  type EventMessage,
  type AuthenticationResult,
  type AccountInfo,
} from '@azure/msal-browser';
import type { Store } from '@reduxjs/toolkit';
import { setUser, clearUser, setAuthError, setAuthLoading, type RootState } from '@store/index';
import { determinePersona, getRolesFromClaims } from './personaUtils';
import { msalInstance } from './msalInstance';
import { telemetry, getAppInsightsInstance } from '@/services/telemetry/telemetry';
import { EVENT_TYPES, AUTH_COMPONENT } from '@/constants';

const ERROR_MESSAGES: Record<string, string> = {
  login_required: 'errors.loginRequired',
  interaction_required: 'errors.interactionRequired',
  consent_required: 'errors.interactionRequired',
  access_denied: 'errors.accessDenied',
  user_cancelled: 'errors.loginFailed',
  no_account_in_silent_request: 'errors.sessionExpired',
  invalid_grant: 'errors.sessionExpired',
};

function getErrorMessage(errorCode: string): string {
  return ERROR_MESSAGES[errorCode] ?? 'errors.unknownError';
}

function createUserFromAccount(account: AccountInfo) {
  const persona = determinePersona(account);
  const roles = account.idTokenClaims
    ? getRolesFromClaims(account.idTokenClaims as Record<string, unknown>)
    : [];

  return {
    id: account.localAccountId,
    email: account.username,
    displayName: account.name ?? account.username,
    persona,
    roles: [...roles],
  };
}

export function registerMsalEventCallbacks(store: Store<RootState>): () => void {
  const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
    handleMsalEvent(event, store);
  });

  return () => {
    if (callbackId) {
      msalInstance.removeEventCallback(callbackId);
    }
  };
}

function handleMsalEvent(event: EventMessage, store: Store<RootState>): void {
  switch (event.eventType) {
    case EventType.HANDLE_REDIRECT_START:
      store.dispatch(setAuthLoading(true));
      break;

    case EventType.ACQUIRE_TOKEN_START:
      store.dispatch(setAuthLoading(true));
      break;

    case EventType.LOGOUT_START:
      telemetry.trackEvent(AUTH_COMPONENT, EVENT_TYPES.START, { operationName: 'Logout' });
      store.dispatch(setAuthLoading(true));
      break;

    case EventType.LOGIN_SUCCESS:
    case EventType.ACQUIRE_TOKEN_SUCCESS: {
      const result = event.payload as AuthenticationResult;
      if (result?.account) {
        const userPayload = createUserFromAccount(result.account);
        store.dispatch(setUser(userPayload));

        if (event.eventType === EventType.LOGIN_SUCCESS) {
          telemetry.trackSuccess(AUTH_COMPONENT, 'Login', {
            userId: result.account.localAccountId,
            persona: userPayload.persona,
          });

          // Set authenticated user context for one-time userId capture (FR-018)
          const ai = getAppInsightsInstance();
          if (ai) {
            ai.setAuthenticatedUserContext(result.account.localAccountId, undefined, true);
          }

          if (result.state) {
            handlePostLoginRedirect(result.state);
          }
        }
      }
      break;
    }

    case EventType.LOGOUT_FAILURE:
    case EventType.ACQUIRE_TOKEN_FAILURE: {
      const error = event.error as { errorCode?: string; message?: string } | undefined;
      if (error) {
        telemetry.trackFailure(AUTH_COMPONENT, 'Login', error.message ?? 'Operation failed', {
          errorCode: error.errorCode ?? 'unknown',
        });
        store.dispatch(
          setAuthError({
            code: error.errorCode ?? 'unknown_error',
            message: getErrorMessage(error.errorCode ?? ''),
          })
        );
      }
      store.dispatch(setAuthLoading(false));
      break;
    }

    case EventType.LOGOUT_SUCCESS:
      store.dispatch(clearUser());
      // Clear authenticated user context on logout (FR-018)
      {
        const ai = getAppInsightsInstance();
        if (ai) {
          ai.clearAuthenticatedUserContext();
        }
      }
      break;

    case EventType.HANDLE_REDIRECT_END:
      handleRedirectComplete(store);
      break;
  }
}

function handleRedirectComplete(store: Store<RootState>): void {
  const account = msalInstance.getActiveAccount();

  if (account) {
    const userPayload = createUserFromAccount(account);
    store.dispatch(setUser(userPayload));
  } else {
    store.dispatch(setAuthLoading(false));
  }
}

export function syncExistingSession(store: Store<RootState>): void {
  const account = msalInstance.getActiveAccount();

  if (account) {
    const userPayload = createUserFromAccount(account);
    store.dispatch(setUser(userPayload));
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
      const activeAccount = accounts[0];
      if (activeAccount) {
        const userPayload = createUserFromAccount(activeAccount);
        store.dispatch(setUser(userPayload));
      }
    } else {
      store.dispatch(setAuthLoading(false));
    }
  }
}

function handlePostLoginRedirect(savedUrl: string): void {
  try {
    const url = new URL(savedUrl);
    const currentUrl = new URL(window.location.href);

    if (
      url.origin === currentUrl.origin &&
      (url.pathname !== currentUrl.pathname || url.search !== currentUrl.search)
    ) {
      window.history.replaceState(null, '', savedUrl);
      window.location.reload();
    }
  } catch {
    /* c8 ignore next 2 */
    // eslint-disable-next-line no-console
    console.debug('[Auth] Invalid saved URL in redirect state:', savedUrl);
  }
}
